import CopyIcon from "@/assets/colorable/copy.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import MailIcon from "@/assets/colorable/mail.svg?react";
import MessageIcon from "@/assets/colorable/message.svg?react";
import WarningTriangleIcon from "@/assets/colorable/warning_triangle.svg?react";
import WhatsappIcon from "@/assets/colorable/whatsapp.svg?react";
import Button from "@/components/Common/Button";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import ReferralCodeForm from "@/components/Common/referral/ReferralCodeForm";
import TextButton from "@/components/Common/TextButton";
import {getAppDownloadLink} from "@/utils/referralUtils";
import {useState, type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useMedia} from "react-use";

const PrivateAmbassadorInvite: FC<{
  agencyId?: number | null;
  companyId?: number | null;
  referralCode: string;
}> = ({agencyId, companyId, referralCode}) => {
  const {t} = useTranslation(["referral", "common"]);
  const [customMessage, setCustomMessage] = useState<string | null>(null);
  const isUnderMd = useMedia("(max-width: 768px)");

  const downloadLink = getAppDownloadLink({referralCode});
  const defaultMessage = t("invite_message_default", {code: referralCode, link: downloadLink});
  const isCustomised = customMessage !== null;

  const message = customMessage ?? defaultMessage;
  const isMessageBlank = !message.trim();

  const renderShareButton = ({icon, label, link}: {icon: ReactNode; label: string; link: string}) => (
    <Button
      buttonStyle="White"
      className="text-neutral-grey-dark h-8 w-fit gap-2 rounded-full px-3 text-xs whitespace-nowrap"
      isDisabled={isMessageBlank}
      onClick={() => window.open(link, "_blank", "noopener,noreferrer")}>
      {icon}
      {label}
    </Button>
  );

  return (
    <>
      <ReferralCodeForm agencyId={agencyId} companyId={companyId} referralCode={referralCode} type="private" />
      <div className="border-warning-sand bg-warning-light-yellow flex gap-3 rounded-md border p-4">
        <WarningTriangleIcon className="text-warning-dark-yellow mt-0.5 size-4 shrink-0" />
        <span className="text-warning-dark-yellow text-xs leading-4.5">
          <span className="font-semibold">{t("invite_private_warning_title")} </span>
          {t("invite_private_warning_description")}
        </span>
      </div>
      <div className="border-primary-light-solid bg-primary-powder flex flex-col gap-3 rounded-md border p-5">
        <div className="flex flex-col gap-1">
          <span className="text-neutral-black text-sm leading-2.5 font-semibold">{t("message_to_share")}</span>
          <span className="text-neutral-grey-dark text-xs">{t("message_to_share_description")}</span>
        </div>
        <AutosizeTextarea
          className="p-4 text-xs leading-5 md:text-sm"
          minRows={6}
          maxRows={10}
          onChange={e => setCustomMessage(e.target.value)}
          value={message}
        />
        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          {isCustomised && (
            <TextButton
              className="text-xs sm:text-sm"
              onClick={() => setCustomMessage(null)}
              text={t("reset_to_default_text")}
            />
          )}
          <CopyToClipboardButton
            buttonStyle="Gradient"
            className="ml-auto h-8 w-fit gap-2 px-5 text-sm whitespace-nowrap"
            contentToCopy={message}
            icon={<CopyIcon className="size-3.5 shrink-0" />}
            isDisabled={isMessageBlank}
            label={t("copy_message")}
            successLabel={t("message_copied")}
          />
        </div>
        <div className="border-primary-light-solid flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-neutral-grey-dark mr-1 text-xs">{t("or_send_directly_via")}</span>
          {renderShareButton({
            icon: <WhatsappIcon className="size-3.5 shrink-0" />,
            label: t("whatsapp"),
            link: `https://wa.me/?text=${encodeURIComponent(message)}`,
          })}
          {renderShareButton({
            icon: <MailIcon className="h-3.5 w-4 shrink-0" />,
            label: t("common:email"),
            link: `mailto:?subject=${encodeURIComponent(t("invite_email_subject"))}&body=${encodeURIComponent(message)}`,
          })}
          {isUnderMd &&
            renderShareButton({
              icon: <MessageIcon className="size-3.5 shrink-0" />,
              label: t("text_message"),
              link: `sms:?body=${encodeURIComponent(message)}`,
            })}
        </div>
      </div>
      <div className="bg-primary-light-solid text-neutral-black flex gap-3 rounded-md p-5">
        <InfoIcon className="text-primary-dark-solid mt-0.5 size-4 shrink-0" />
        <div className="flex flex-col gap-2">
          <span className="text-sm leading-2.5 font-semibold">{t("how_it_works")}</span>
          <span className="text-xs">{t("how_it_works_description")}</span>
        </div>
      </div>
    </>
  );
};

export default PrivateAmbassadorInvite;
